import {
  computeDpopAccessTokenHash,
  computeDpopJwkThumbprint,
  DpopPublicJwk,
} from "@amoreg/shared/dpop";
import * as Crypto from "effect/Crypto";
import * as Data from "effect/Data";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Schema from "effect/Schema";
import { importJWK, SignJWT, type JWK } from "jose";

export interface BrowserDpopKey {
  readonly privateKey: CryptoKey;
  readonly publicJwk: DpopPublicJwk;
  readonly thumbprint: string;
}

export class BrowserDpopError extends Data.TaggedError("BrowserDpopError")<{
  readonly message: string;
  readonly cause?: unknown;
}> {}

/**
 * IndexedDB database holding this browser profile's DPoP proof key. Exported
 * so the sign-out purge can delete it by the same name that creates it.
 */
export const DPOP_DATABASE_NAME = "amoreg:cloud-auth";
const DPOP_DATABASE_VERSION = 1;
const DPOP_KEY_STORE_NAME = "keys";
const DPOP_KEY_ID = "relay-dpop-proof-key";
const decodeDpopPublicJwk = Schema.decodeUnknownEffect(DpopPublicJwk);

/**
 * Process-lifetime cache of the loaded proof key. It lives in this module
 * rather than in a consumer's closure so sign-out has one place to drop it:
 * a still-open page must not keep proving with a key whose database was just
 * deleted.
 */
let cachedBrowserDpopKey: BrowserDpopKey | null = null;
// Bumped by `clearBrowserDpopKeyCache` so a read or write that was already in
// flight at sign-out cannot repopulate the cache with the revoked key.
let dpopCacheGeneration = 0;

/**
 * Forgets the in-memory proof key. Called by the sign-out storage purge
 * alongside deleting {@link DPOP_DATABASE_NAME}; the next relay use lazily
 * generates a fresh key.
 */
export function clearBrowserDpopKeyCache(): void {
  dpopCacheGeneration += 1;
  cachedBrowserDpopKey = null;
}

/**
 * Sign-out in one tab must revoke the key in every tab: the IndexedDB
 * database is shared, but each tab's module cache is not. The purge posts on
 * this channel and every tab's listener clears its own cache.
 */
export const DPOP_PURGE_CHANNEL_NAME = "amoreg:cloud-auth:purge";
const DPOP_PURGE_MESSAGE = "purge";

function openDpopPurgeChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") {
    return null;
  }
  try {
    return new BroadcastChannel(DPOP_PURGE_CHANNEL_NAME);
  } catch {
    return null;
  }
}

/** Tell every other tab to drop its cached DPoP key (this tab clears its own directly). */
export function broadcastBrowserDpopKeyPurge(): void {
  const channel = openDpopPurgeChannel();
  if (channel === null) {
    return;
  }
  try {
    channel.postMessage(DPOP_PURGE_MESSAGE);
  } finally {
    channel.close();
  }
}

/** Subscribe this tab to purge broadcasts; returns the unsubscribe. Installed once at module load. */
export function listenForBrowserDpopKeyPurge(): () => void {
  const channel = openDpopPurgeChannel();
  if (channel === null) {
    return () => {};
  }
  const onMessage = (event: MessageEvent) => {
    if (event.data === DPOP_PURGE_MESSAGE) {
      clearBrowserDpopKeyCache();
    }
  };
  channel.addEventListener("message", onMessage);
  return () => {
    channel.removeEventListener("message", onMessage);
    channel.close();
  };
}

listenForBrowserDpopKeyPurge();

export const browserCryptoLayer = Layer.succeed(
  Crypto.Crypto,
  Crypto.make({
    randomBytes: (size) => globalThis.crypto.getRandomValues(new Uint8Array(size)),
    digest: (algorithm, data) =>
      Effect.promise(async () => {
        const input = new Uint8Array(data.length);
        input.set(data);
        return new Uint8Array(await globalThis.crypto.subtle.digest(algorithm, input.buffer));
      }),
  }),
);

function dpopError(message: string, cause?: unknown) {
  return new BrowserDpopError({ message, ...(cause === undefined ? {} : { cause }) });
}

function openDpopDatabase(): Effect.Effect<IDBDatabase, BrowserDpopError> {
  return Effect.callback<IDBDatabase, BrowserDpopError>((resume) => {
    const request = indexedDB.open(DPOP_DATABASE_NAME, DPOP_DATABASE_VERSION);
    request.addEventListener("error", () =>
      resume(
        Effect.fail(dpopError("Could not open DPoP key storage.", request.error ?? undefined)),
      ),
    );
    request.addEventListener("upgradeneeded", () => {
      if (!request.result.objectStoreNames.contains(DPOP_KEY_STORE_NAME)) {
        request.result.createObjectStore(DPOP_KEY_STORE_NAME);
      }
    });
    request.addEventListener("success", () => resume(Effect.succeed(request.result)));
  });
}

export function readStoredBrowserDpopKey(): Effect.Effect<BrowserDpopKey | null, BrowserDpopError> {
  if (cachedBrowserDpopKey) {
    return Effect.succeed(cachedBrowserDpopKey);
  }
  if (typeof indexedDB === "undefined") {
    return Effect.succeed(null);
  }
  const generation = dpopCacheGeneration;
  return Effect.acquireUseRelease(
    openDpopDatabase(),
    (database) =>
      Effect.callback<BrowserDpopKey | null, BrowserDpopError>((resume) => {
        const request = database
          .transaction(DPOP_KEY_STORE_NAME, "readonly")
          .objectStore(DPOP_KEY_STORE_NAME)
          .get(DPOP_KEY_ID);
        request.addEventListener("error", () =>
          resume(Effect.fail(dpopError("Could not read DPoP key.", request.error ?? undefined))),
        );
        request.addEventListener("success", () => {
          const stored = (request.result as BrowserDpopKey | undefined) ?? null;
          // A purge that ran while this read was pending wins: the key it
          // returned was revoked at sign-out and must not be cached or used.
          // The caller is failed rather than told "no key": a `null` reads as
          // a cache miss, and the relay signer answers a miss by generating a
          // fresh key and writing it back — which would leave key material in
          // IndexedDB after the sign-out purge deleted the database.
          if (generation !== dpopCacheGeneration) {
            resume(Effect.fail(dpopError("DPoP key read was invalidated by sign-out.")));
            return;
          }
          if (stored) {
            cachedBrowserDpopKey = stored;
          }
          resume(Effect.succeed(stored));
        });
      }),
    (database) => Effect.sync(() => database.close()),
  );
}

export function writeStoredBrowserDpopKey(
  key: BrowserDpopKey,
): Effect.Effect<void, BrowserDpopError> {
  if (typeof indexedDB === "undefined") {
    cachedBrowserDpopKey = key;
    return Effect.void;
  }
  const generation = dpopCacheGeneration;
  return Effect.acquireUseRelease(
    openDpopDatabase(),
    (database) =>
      Effect.callback<void, BrowserDpopError>((resume) => {
        // A purge between capturing the generation and opening the write
        // transaction wins: the database this write would repopulate was
        // deleted at sign-out, so the key is never put, not merely left out
        // of the cache.
        if (generation !== dpopCacheGeneration) {
          resume(Effect.fail(dpopError("DPoP key write was invalidated by sign-out.")));
          return;
        }
        const transaction = database.transaction(DPOP_KEY_STORE_NAME, "readwrite");
        transaction.addEventListener("error", () =>
          resume(
            Effect.fail(dpopError("Could not write DPoP key.", transaction.error ?? undefined)),
          ),
        );
        // The cache follows the store: a failed write leaves it untouched (so
        // the next read retries), and a purge during the write wins.
        transaction.addEventListener("complete", () => {
          if (generation === dpopCacheGeneration) {
            cachedBrowserDpopKey = key;
          }
          resume(Effect.void);
        });
        transaction.objectStore(DPOP_KEY_STORE_NAME).put(key, DPOP_KEY_ID);
      }),
    (database) => Effect.sync(() => database.close()),
  );
}

export const generateBrowserDpopKey = Effect.gen(function* () {
  const generated = yield* Effect.tryPromise({
    try: () =>
      crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, true, [
        "sign",
        "verify",
      ]) as Promise<CryptoKeyPair>,
    catch: (cause) => dpopError("Could not generate DPoP proof key.", cause),
  });
  const privateJwk = yield* Effect.tryPromise({
    try: () => crypto.subtle.exportKey("jwk", generated.privateKey),
    catch: (cause) => dpopError("Could not export DPoP private key.", cause),
  });
  const publicJwk = yield* Effect.tryPromise({
    try: () => crypto.subtle.exportKey("jwk", generated.publicKey),
    catch: (cause) => dpopError("Could not export DPoP public key.", cause),
  }).pipe(
    Effect.flatMap((jwk) => decodeDpopPublicJwk(jwk)),
    Effect.mapError((cause) =>
      cause instanceof BrowserDpopError
        ? cause
        : dpopError("Generated DPoP public key is invalid.", cause),
    ),
  );
  const privateKey = yield* Effect.tryPromise({
    try: () => importJWK(privateJwk as JWK, "ES256", { extractable: false }) as Promise<CryptoKey>,
    catch: (cause) => dpopError("Could not import DPoP private key.", cause),
  });
  return {
    privateKey,
    publicJwk,
    thumbprint: computeDpopJwkThumbprint(publicJwk),
  };
});

export function createBrowserDpopProof(input: {
  readonly method: string;
  readonly url: string;
  readonly accessToken?: string;
  readonly proofKey: BrowserDpopKey;
}): Effect.Effect<
  { readonly proof: string; readonly thumbprint: string },
  BrowserDpopError,
  Crypto.Crypto
> {
  return Effect.gen(function* () {
    const normalizedUrl = yield* Effect.try({
      try: () => new URL(input.url),
      catch: (cause) => dpopError("Could not normalize DPoP proof URL.", cause),
    });
    normalizedUrl.search = "";
    normalizedUrl.hash = "";
    const jti = yield* Crypto.Crypto.pipe(
      Effect.flatMap((crypto) => crypto.randomUUIDv4),
      Effect.mapError((cause) => dpopError("Could not generate DPoP proof identifier.", cause)),
    );
    const proof = yield* Effect.tryPromise({
      try: () =>
        new SignJWT({
          htm: input.method.toUpperCase(),
          htu: normalizedUrl.toString(),
          jti,
          ...(input.accessToken ? { ath: computeDpopAccessTokenHash(input.accessToken) } : {}),
        })
          .setProtectedHeader({
            typ: "dpop+jwt",
            alg: "ES256",
            jwk: input.proofKey.publicJwk,
          })
          .setIssuedAt()
          .sign(input.proofKey.privateKey),
      catch: (cause) => dpopError("Could not sign DPoP proof.", cause),
    });
    return { proof, thumbprint: input.proofKey.thumbprint };
  });
}
